/**
 * Referral Reports chart model (2026-08 ticket).
 *
 * Pure helpers — no React, no network. The chart aggregates the /run
 * result set by TIME BUCKET (independent of the group-by dimensions);
 * the results table keeps full group-by detail and is untouched.
 *
 * Bucket keys arrive from the report engine as plain strings, already
 * truncated in America/Los_Angeles (report_engine `_GRAIN_LABEL_EXPRS`):
 *   • week  → 'YYYY-MM-DD'  (ISO week, Monday start)
 *   • month → 'YYYY-MM'
 *   • day   → 'YYYY-MM-DD'  (not advertised by the engine today)
 * All arithmetic below runs on those naive Y-M-D parts via Date.UTC, so
 * the browser's local timezone can never shift a boundary. Axis labels
 * are derived from real bucket boundaries — never re-parsed from a
 * formatted row value.
 */

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const OTHER_SERIES = "Other";
export const BLANK_SERIES = "(blank)";
export const MAX_SERIES = 8;      // 7 named + "Other"
export const MAX_TICKS = 12;

// `ytd` is month-bucketed (Jan 1 → today); the engine's own `ytd` grain
// buckets by YEAR, so the page requests `month` and a YTD range.
export const effectiveGrain = (grain) => (grain === "ytd" ? "month" : grain);

function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(String(s || ""));
  if (!m) return null;
  return {
    y: parseInt(m[1], 10),
    m: parseInt(m[2], 10),
    d: m[3] ? parseInt(m[3], 10) : 1,
  };
}

const utc = (y, m, d) => Date.UTC(y, m - 1, d);
const pad = (n) => String(n).padStart(2, "0");
const ymdOf = (ts) => {
  const dt = new Date(ts);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

/** Monday (ISO week start) of the day containing `ts`. */
function isoWeekStart(ts) {
  const dow = new Date(ts).getUTCDay();      // 0=Sun … 6=Sat
  return ts - ((dow + 6) % 7) * 86400000;
}

/**
 * Ordered bucket keys covering [dateFrom, dateTo) — dateTo is EXCLUSIVE
 * (report_engine compiles `< date_to`). Empty buckets are included so
 * the caller can zero-fill them.
 */
export function bucketKeysForRange(grain, dateFrom, dateTo) {
  const g = effectiveGrain(grain);
  const from = parseYmd(dateFrom);
  const to = parseYmd(dateTo);
  if (!from || !to) return [];
  const startTs = utc(from.y, from.m, from.d);
  // exclusive end → last covered day
  const endTs = utc(to.y, to.m, to.d) - 86400000;
  if (endTs < startTs) return [];

  const keys = [];
  if (g === "month") {
    const endDt = new Date(endTs);
    let y = from.y;
    let m = from.m;
    const lastY = endDt.getUTCFullYear();
    const lastM = endDt.getUTCMonth() + 1;
    while (y < lastY || (y === lastY && m <= lastM)) {
      keys.push(`${y}-${pad(m)}`);
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
    return keys;
  }
  if (g === "week") {
    for (let ts = isoWeekStart(startTs); ts <= endTs; ts += 7 * 86400000) {
      keys.push(ymdOf(ts));
    }
    return keys;
  }
  if (g === "day") {
    for (let ts = startTs; ts <= endTs; ts += 86400000) keys.push(ymdOf(ts));
    return keys;
  }
  return [];
}

/** True when the bucket set straddles more than one calendar year. */
export function spansYears(bucketKeys) {
  const years = new Set(bucketKeys.map((k) => String(k).slice(0, 4)));
  return years.size > 1;
}

/** Axis / tooltip label for a bucket boundary. */
export function formatBucketTick(key, grain, withYear = false) {
  const g = effectiveGrain(grain);
  const p = parseYmd(key);
  if (!p) return key == null ? "" : String(key);
  const mon = MONTHS_SHORT[p.m - 1];
  if (g === "month") return `${mon} ${p.y}`;
  if (g === "week") return withYear ? `Wk of ${mon} ${p.d}, ${p.y}` : `Wk of ${mon} ${p.d}`;
  if (g === "day") return withYear ? `${mon} ${p.d}, ${p.y}` : `${mon} ${p.d}`;
  return String(key);
}

/** Recharts XAxis `interval` that keeps visible ticks at ~MAX_TICKS. */
export function tickInterval(bucketCount, maxTicks = MAX_TICKS) {
  if (!bucketCount || bucketCount <= maxTicks) return 0;
  return Math.ceil(bucketCount / maxTicks) - 1;
}

const seriesNameOf = (v) => {
  if (v === null || v === undefined || v === "") return BLANK_SERIES;
  return String(v);
};

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

/**
 * Aggregate result rows into one datum per time bucket.
 *
 * dimensionField == null → one series per measure (the "None (total)"
 * mode). dimensionField set → one series per distinct dimension value
 * of measureFields[0], summed across every other group-by combination,
 * capped at 7 named series + "Other".
 *
 * Returns { data, series, truncated }. `data[i]` always carries a value
 * for every series (0 when the bucket has no rows).
 */
export function buildChartSeries({
  rows = [],
  bucketField,
  measureFields = [],
  dimensionField = null,
  bucketKeys = [],
  maxSeries = MAX_SERIES,
}) {
  if (!bucketField || !measureFields.length) {
    return { data: [], series: [], truncated: false };
  }

  // Bucket order: the enumerated range first (zero-filled), then any
  // bucket present in the data but outside the enumerated range.
  const order = bucketKeys.slice();
  const known = new Set(order);
  for (const r of rows) {
    const b = r?.[bucketField];
    if (b != null && b !== "" && !known.has(String(b))) {
      known.add(String(b));
      order.push(String(b));
    }
  }

  if (!dimensionField) {
    const byBucket = new Map(order.map((b) => [b, Object.fromEntries(
      measureFields.map((mf) => [mf, 0]))]));
    for (const r of rows) {
      const b = String(r?.[bucketField] ?? "");
      const slot = byBucket.get(b);
      if (!slot) continue;
      for (const mf of measureFields) slot[mf] += num(r[mf]);
    }
    return {
      data: order.map((b) => ({ bucket: b, ...byBucket.get(b) })),
      series: measureFields.slice(),
      truncated: false,
    };
  }

  const measureField = measureFields[0];
  const totalsByValue = new Map();
  for (const r of rows) {
    const name = seriesNameOf(r?.[dimensionField]);
    totalsByValue.set(name, (totalsByValue.get(name) || 0) + num(r[measureField]));
  }
  const ranked = [...totalsByValue.entries()]
    .sort((a, b) => (b[1] - a[1]) || String(a[0]).localeCompare(String(b[0])))
    .map(([name]) => name);

  const truncated = ranked.length > maxSeries;
  const named = truncated ? ranked.slice(0, maxSeries - 1) : ranked;
  const namedSet = new Set(named);
  const series = truncated ? [...named, OTHER_SERIES] : named;

  const byBucket = new Map(order.map((b) => [b, Object.fromEntries(
    series.map((s) => [s, 0]))]));
  for (const r of rows) {
    const b = String(r?.[bucketField] ?? "");
    const slot = byBucket.get(b);
    if (!slot) continue;
    const name = seriesNameOf(r?.[dimensionField]);
    const key = namedSet.has(name) ? name : OTHER_SERIES;
    if (!(key in slot)) continue;
    slot[key] += num(r[measureField]);
  }

  return {
    data: order.map((b) => ({ bucket: b, ...byBucket.get(b) })),
    series,
    truncated,
  };
}

/**
 * Aggregate vs raw-list mode, read off the /run response itself (not the
 * builder state, which can drift after the run).
 */
export function isGroupedResult(columns = []) {
  return columns.some((c) => c.kind === "measure"
    || (c.kind === "date" && !!c.grain));
}

/**
 * Honest header count. In aggregate mode `total` is the number of
 * GROUPED ROWS (the engine counts the grouped subquery), NOT referrals —
 * "groups" is used rather than "rows" because "rows" collides with the
 * paginated `row_count` shown next to it. Raw-list mode keeps
 * "referrals": there, one row IS one referral.
 */
export function resultCountLabel(total, grouped) {
  const n = Number(total) || 0;
  const noun = grouped ? "group" : "referral";
  return `${noun}${n === 1 ? "" : "s"}`;
}

/**
 * Intake-row context line for the results header (R3, 2026-08-25).
 * `n` = intake-stage rows inside THIS result. Returns null when there
 * are none so the line is absent entirely. Neutral, informational
 * wording — it answers "why did counts rise?" and "why are some columns
 * blank on some rows?" without sounding like a warning.
 */
export function intakeRowsNote(n) {
  const count = Number(n) || 0;
  if (count <= 0) return null;
  return `Includes ${count} pending/incomplete intake referral${count === 1 ? "" : "s"}`;
}

/** Sum of every series across every bucket (invariant check + display). */export function grandTotal(data, series) {
  let t = 0;
  for (const d of data) for (const s of series) t += num(d[s]);
  return t;
}

export const SERIES_COLORS = [
  "#0f172a", "#059669", "#dc2626", "#0284c7", "#d97706",
  "#7c3aed", "#0891b2", "#be123c",
];
